import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const PRODUCTLY_VIOLET = '#7C3AED';

/* ─── Shared sub-components ───────────────────────────────────────────── */
const ProductlyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/productly"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Productly
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: PRODUCTLY_VIOLET }}>
          <LayoutGrid size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Productly</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const ProductlyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Productly · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/productly/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/productly/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#7C3AED] uppercase">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest text-[#7C3AED] uppercase shrink-0 mt-1.5">{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

const SubHeading = ({ children }) => (
  <h3 className="font-mono text-[0.65rem] tracking-widest text-[#374151] uppercase mb-2 mt-4">{children}</h3>
);

const DataPoint = ({ children }) => (
  <li className="text-[#374151] text-sm leading-relaxed flex items-start gap-2">
    <span className="w-1 h-1 rounded-full bg-[#7C3AED] shrink-0 mt-2" />
    {children}
  </li>
);

/* ─── Page ────────────────────────────────────────────────────────────── */
export const ProductlyPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Productly Privacy Policy"
        description="Privacy policy for the Productly Shopify app by Smart Solutions."
        path="/apps/productly/privacy"
      />
      <ProductlyTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Privacy Policy
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: May 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              This Privacy Policy explains how Productly, operated by Smart Solutions, collects, uses, and protects data
              from merchants who install the App. By installing or using Productly, you accept the practices described
              in this policy. For any privacy questions, contact us at{' '}
              <a href="mailto:hello@smartsolution.ai" className="text-[#7C3AED] hover:underline">hello@smartsolution.ai</a>.
            </P>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Introduction">
            <P>
              Productly is a Shopify embedded app developed and operated by Smart Solutions that enriches product pages
              with custom content blocks, spec tables, trust badges, FAQs, tabbed content, and extra media.
            </P>
            <P>
              This policy covers data we collect and process in connection with the App. Installing or using Productly
              means you accept this Privacy Policy.
            </P>
          </Section>

          <Section num="02" title="Information We Collect">
            <SubHeading>Shop information from Shopify</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Shop domain and shop ID</DataPoint>
              <DataPoint>Store owner email</DataPoint>
              <DataPoint>Store locale, currency, and plan</DataPoint>
            </ul>

            <SubHeading>Product & store data needed to operate</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Product titles, descriptions, images, and variants</DataPoint>
              <DataPoint>Metafields and translations</DataPoint>
            </ul>

            <SubHeading>Content the merchant provides</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>URLs, prompts, settings, and other inputs you enter into the App</DataPoint>
            </ul>

            <SubHeading>Usage data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Actions taken, jobs run, and features used</DataPoint>
              <DataPoint>Timestamps and error logs</DataPoint>
            </ul>

            <SubHeading>Billing data from Shopify Billing API</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Subscription state and credit balance — we never receive or store card details</DataPoint>
            </ul>

            <SubHeading>Technical data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>IP address, browser, device, and session identifiers</DataPoint>
            </ul>

            <P>
              <strong className="font-medium text-[#0F172A]">We do not process Shopify customer personal data (PII).</strong>{' '}
              Productly operates on product and store content only and does not collect names, emails, addresses, or payment
              information of your customers.
            </P>
          </Section>

          <Section num="03" title="How We Use Information">
            <ul className="space-y-2 mb-4">
              <DataPoint>Operate and maintain the App</DataPoint>
              <DataPoint>Process merchant inputs and return outputs</DataPoint>
              <DataPoint>Manage your account, subscription, and credits, and prevent abuse</DataPoint>
              <DataPoint>Monitor performance, debug issues, and improve reliability</DataPoint>
              <DataPoint>Provide support and send service communications</DataPoint>
              <DataPoint>Comply with legal obligations and enforce our Terms</DataPoint>
            </ul>
          </Section>

          <Section num="04" title="Legal Bases for Processing (GDPR)">
            <ul className="space-y-2 mb-4">
              <DataPoint><strong className="font-medium text-[#0F172A]">Contract performance</strong> — to provide the App you installed</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Legitimate interests</strong> — to maintain, secure, and improve the App</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Legal obligation</strong> — to comply with applicable law</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Consent</strong> — where required</DataPoint>
            </ul>
          </Section>

          <Section num="05" title="Sharing of Information">
            <P>We do not sell your data. We share information only as follows:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint><strong className="font-medium text-[#0F172A]">Shopify</strong> — the platform the App runs on</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Service providers / sub-processors</strong> — hosting, storage, email, error monitoring, and AI processing</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Professional advisors</strong> — legal and accounting, where necessary</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Authorities</strong> — when required by law</DataPoint>
            </ul>
          </Section>

          <Section num="06" title="International Data Transfers">
            <P>
              Your data may be processed in countries outside your own. Where data is transferred internationally, we rely
              on appropriate safeguards such as standard contractual clauses.
            </P>
          </Section>

          <Section num="07" title="Data Retention">
            <P>
              We retain data while the App is installed and for a limited period afterwards. Following uninstall, data is
              deleted or anonymized within a reasonable period.
            </P>
            <P>
              We may retain certain data longer where required for legal compliance or dispute resolution.
            </P>
          </Section>

          <Section num="08" title="Your Rights">
            <P>Depending on your location, you may have the right to:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint>Access, correct, or delete your data</DataPoint>
              <DataPoint>Restrict or object to processing</DataPoint>
              <DataPoint>Request data portability</DataPoint>
            </ul>
            <P>
              To exercise these rights, contact us at{' '}
              <a href="mailto:hello@smartsolution.ai" className="text-[#7C3AED] hover:underline">hello@smartsolution.ai</a>.
              You also have the right to lodge a complaint with your data protection authority.
            </P>
            <P>
              We honour Shopify's mandatory GDPR webhooks:{' '}
              <strong className="font-medium text-[#0F172A]">customers/data_request</strong>,{' '}
              <strong className="font-medium text-[#0F172A]">customers/redact</strong>, and{' '}
              <strong className="font-medium text-[#0F172A]">shop/redact</strong>.
            </P>
          </Section>

          <Section num="09" title="Security">
            <P>
              We apply reasonable technical and organizational measures to protect data. However, no method of transmission
              or storage is completely secure, and we cannot guarantee absolute security.
            </P>
          </Section>

          <Section num="10" title="Cookies & Similar Technologies">
            <P>
              We use cookies and similar technologies for authentication, session management, and analytics. You can control
              cookies through your browser settings.
            </P>
          </Section>

          <Section num="11" title="Children's Privacy">
            <P>
              The App is not directed at children, and we do not knowingly collect personal data from minors.
            </P>
          </Section>

          <Section num="12" title="Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. We will notify merchants of significant changes via the
              Shopify admin or email. The "last updated" date at the top of this page reflects the most recent revision.
            </P>
          </Section>

          <Section num="13" title="Contact Us">
            <P>For any privacy questions or data requests:</P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" className="text-[#7C3AED] hover:underline">hello@smartsolution.ai</a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              This policy was last updated in May 2026
            </p>
            <Link
              to="/apps/productly/terms"
              className="font-mono text-[0.6rem] tracking-widest text-[#7C3AED] hover:underline uppercase"
            >
              View Terms &amp; Conditions →
            </Link>
          </div>
        </div>
      </div>

      <ProductlyFooter />
    </div>
  );
};
